import {useTooltipsStore} from "@/stores/ToastDataStore.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import {useToast} from "vue-toastification";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import Translator from "@/utils/common/Translator.ts";
import EmbeddedViewUtils from "@/utils/editView/editScreens/EmbeddedViewUtils.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import GenericValues from "@/utils/common/GenericValues.ts";
import {CellKeyDownEvent, FullWidthCellKeyDownEvent, GridApi} from "ag-grid-enterprise";
import {TooltipContext} from "@/utils/UIConstants/enums.ts";
import {tooltipParams} from "@/utils/presenters/interfaces.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import type ListPresenter from "@/utils/common/ListPresenter.ts";
import type SearcherViewPresenter from "@/utils/common/SearcherViewPresenter.ts";
import {Dialog} from "quasar";
import TooltipsListDialog from "@/components/dialogs/TooltipsListDialog.vue";
import TooltipMetadata = namaTypes.TooltipMetadata;
import FieldTooltipInfo = namaTypes.FieldTooltipInfo;
import GUIRunTooltipReq = namaTypes.GUIRunTooltipReq;
import GenericValue = namaTypes.GenericValue;
import NaMaMetaData = namaTypes.NaMaMetaData;

export default class TooltipUtils {
  private field?: string;
  private fallBackId?: string;
  private lineNumber: number = -100;
  private static _instance: TooltipUtils;

  static instance() {
    if (!this._instance)
      this._instance = new TooltipUtils();
    return this._instance;
  }

  isShowingForField(field: string, fallBackId: string, lineNumber: number): boolean {
    return field === this.field && fallBackId === this.fallBackId && lineNumber == this.lineNumber;
  }

  displayTooltip(message: string, field: string, fallBackId: string, lineNumber: number) {
    this.field = field;
    this.fallBackId = fallBackId;
    this.lineNumber = lineNumber;
    useTooltipsStore().pushTooltipStr(message);
  }

  hideTooltip() {
    this.field = '';
    this.fallBackId = '';
    this.lineNumber = -100;
    useToast().dismiss("nama-tooltip-toast");
    useTooltipsStore().clearTooltips();
  }

  static fetchFieldTooltip(entityType: string, fieldId: string, fallBackId: string, automaticTooltipsOnly: boolean,
                           context: TooltipContext = TooltipContext.EditView): FieldTooltipInfo[] | undefined {
    let tooltipMetadata: TooltipMetadata | undefined = ClientCache.getTooltipMetadata();//
    let fieldTooltipInfo: FieldTooltipInfo[] = this.fetchFieldTooltipInfo(tooltipMetadata, entityType, fieldId);
    if (fallBackId && !(fieldTooltipInfo && fieldTooltipInfo.length))
      fieldTooltipInfo = this.fetchFieldTooltipInfo(tooltipMetadata, entityType, fallBackId);
    if (!fieldTooltipInfo)
      return;
    fieldTooltipInfo = fieldTooltipInfo.filter(l => this.runsInContext(l, context));
    if (automaticTooltipsOnly)
      fieldTooltipInfo = fieldTooltipInfo.filter(l => l.automaticTooltip);
    if (!fieldTooltipInfo.length)
      return;
    return fieldTooltipInfo;
  }

  private static runsInContext(tooltip: FieldTooltipInfo, context: TooltipContext): boolean {
    switch (context) {
      case TooltipContext.ListView:
        return !!tooltip.runInListView;
      case TooltipContext.Searcher:
        return !!tooltip.runInSearcher;
      case TooltipContext.EditView:
      default:
        return !tooltip.doNotRunInEditView;
    }
  }

  static async runTooltip(params: tooltipParams) {
    let util = this.instance();
    if (!params.automaticTooltipsOnly && util.isShowingForField(params.field, params.fallBackId, params.lineNumber)) {
      util.hideTooltip();
      return;
    }
    let fieldTooltips = this.fetchFieldTooltip(params.entityType, params.field, params.fallBackId, params.automaticTooltipsOnly, params.context);
    if (!fieldTooltips)
      return;
    let request = params.buildRequest(fieldTooltips);
    let result = await NamaCommon.namaFetch(params.url, {data: request});
    if (result && !result.failureOccurred) {
      util.displayTooltip(Translator.translateNaMaText(result.data), params.field, params.fallBackId, params.lineNumber);
    }
  }

  private static fetchFieldTooltipInfo(tooltipMetadata: TooltipMetadata | undefined, entityType: string, field: string): FieldTooltipInfo[] {
    if (!tooltipMetadata)
      return [];
    let fieldTooltips: FieldTooltipInfo[];
    let fieldsTooltipById: Record<any, any> = tooltipMetadata.fieldsTooltips ? tooltipMetadata.fieldsTooltips[entityType + '#' + field] : {};
    fieldTooltips = fieldsTooltipById?.list || [];
    let defaultTooltips: Record<any, any> = tooltipMetadata.defaultTooltips ? tooltipMetadata.defaultTooltips[field] : {};
    let joinedTooltips: FieldTooltipInfo[] = fieldTooltips.concat((defaultTooltips && defaultTooltips.list) ? defaultTooltips.list : []);
    return this.getTooltip(joinedTooltips, tooltipMetadata);
  }

  static getTooltip(joinedTooltips: Record<string, any>[], tooltipMetadata: TooltipMetadata) {
    if (!joinedTooltips || !tooltipMetadata.fieldTooltips || !tooltipMetadata.fieldTooltips.length)
      return [];
    return joinedTooltips.map(r => tooltipMetadata.fieldTooltips![r.index]);
  }

  static collectTooltipFields(tooltips: FieldTooltipInfo[]): string[] {
    let fields: string[] = [];
    tooltips.forEach(tooltip => fields.push(...EmbeddedViewUtils.collectFieldsFromParameterMapping(tooltip)));
    return Array.from(new Set(fields));
  }

  static async runListTooltip(event: CellKeyDownEvent | FullWidthCellKeyDownEvent, entityType: string,
                              context: TooltipContext, metadata: namaTypes.NaMaMetaData) {
    if ((event.event as KeyboardEvent)?.key !== 'F9')
      return;
    let colDef = event.api.getFocusedCell()?.column?.getColDef();
    if (!colDef)
      return;
    let field = colDef.context?.fieldId ?? colDef.colId ?? '';
    await this.runListTooltipForField(event.api, entityType, context, metadata, field, event.rowIndex ?? -1);
  }

  static async runListTooltipForField(api: GridApi, entityType: string, context: TooltipContext,
                                      metadata: NaMaMetaData, field: string, lineNumber: number) {
    let rowData = api.getDisplayedRowAtIndex(lineNumber)?.data;
    let recordId = rowData ? rowData[CommonFieldIds.ID] : undefined;
    await this.runTooltip({
      entityType, field, fallBackId: entityType, lineNumber,
      automaticTooltipsOnly: false, context, url: NamaURLS.RunTooltip,
      buildRequest: (tooltips: FieldTooltipInfo[]) =>
        this.buildListTooltipRequest(field, lineNumber, entityType, recordId, api, rowData, metadata, tooltips)
    });
  }

  static buildListTooltipRequest(requester: string, lineNumber: number, entityType: string, recordId: string | undefined,
                                 api: any, rowData: Record<string, any> | undefined, metadata: NaMaMetaData,
                                 tooltips: FieldTooltipInfo[]): GUIRunTooltipReq {
    let parameters: Record<string, GenericValue> = {};
    let fieldsToFetch: string[] = [];
    for (let field of this.collectTooltipFields(tooltips)) {
      let column = api?.getColumn(field);
      let value = rowData ? rowData[field] : undefined;
      if (column && column.isVisible() && value !== undefined && value !== null && typeof value !== 'object')
        parameters[field] = GenericValues.createGenericValue(field, metadata, value);
      else
        fieldsToFetch.push(field);
    }
    return {lineNumber, requester, targetEntityType: entityType, tooltipInfo: tooltips, recordId, fieldsToFetch, parameters};
  }

  static fetchTooltipFieldIds(entityType: string | undefined, context: TooltipContext): string[] {
    return ClientCache.fetchTooltipFieldIds(entityType)
      .filter(id => !!this.fetchFieldTooltip(entityType ?? '', id, '', false, context));
  }

  static preValidateTooltipsList(entityType: string | undefined, context: TooltipContext = TooltipContext.EditView): boolean {
    return this.fetchTooltipFieldIds(entityType, context).length > 0;
  }

  static async showTooltipsList(presenter: EditPresenter | ListPresenter | SearcherViewPresenter, context: TooltipContext = TooltipContext.EditView) {
    Dialog.create({
      component: TooltipsListDialog,
      componentProps: {
        title: 'tooltips',
        presenter,
        context
      }
    });
  }
}
